<template>
  <SafeTeleport to="#status-dropdown-target">
    <div>
      <Dropdown
        :items="statusOptions"
        :label="label"
        :disabled="isDisabled"
        @update:model-value="handleStatusSelect"
      />
      <Modal
        v-if="modalOpen"
        title="Appointment Status Change"
        :submit-btn-text="canOverrideCloseout ? 'Complete anyway' : undefined"
        :disable-submit="canOverrideCloseout && !reason.trim()"
        @hide-modal="hideModal"
        @submit="handleSave"
      >
        <div>
          <CustomFormCloseoutBlock
            v-if="closeoutGaps"
            v-model:overriding="overrideCloseout"
            class="mb-3"
            :gaps="closeoutGaps"
            :job-id="jobId"
            :can-link="canOpenFormsTab"
            :overridable="true"
          />
          <Alert type="warning" class="mb-3">
            <div>
              <p v-if="lostFields.length">
                By changing the status to "<span class="font-bold">{{
                  selectedStatus
                }}</span
                >" the following times will be reset.
              </p>
              <p v-else-if="requiredFields.length">
                By changing the status to "<span class="font-bold">{{
                  selectedStatus
                }}</span
                >" you will need to update the times below to reflect the
                correct time.
              </p>
              <template v-if="techImpact">
                <hr class="my-2 border-orange-200" />
                <p
                  class="text-xs font-semibold uppercase tracking-wide text-orange-800/70 mb-1.5"
                >
                  Affected Technicians
                </p>
                <ul class="space-y-1 text-sm">
                  <li
                    v-for="item in techImpact.items"
                    :key="item.name"
                    class="flex items-baseline gap-1.5"
                  >
                    <span class="text-orange-400 text-[8px] leading-none mt-px"
                      >&#9679;</span
                    >
                    <span>
                      <span class="font-medium">{{ item.name }}</span>
                      <span
                        v-if="item.isPrimary"
                        class="text-xs text-gray-500 ml-0.5"
                        >(default)</span
                      >
                      <span class="text-gray-400 mx-0.5">&mdash;</span>
                      <span class="text-gray-600">{{ item.description }}</span>
                    </span>
                  </li>
                </ul>
                <p class="mt-2 text-xs leading-snug text-gray-500">
                  Each technician's status will update to match.<template
                    v-if="techImpact.hasCrewMembers"
                  >
                    Crew member times will sync with their assigned
                    technician.</template
                  >
                </p>
              </template>
            </div>
          </Alert>
          <AppointmentTimeline
            :appointment="appointment"
            :editable-times="requiredFields"
            :lost-times="lostFields"
            @update:model-value="handleUpdateTime"
          />
          <!-- One field, two destinations. In the override state its value is
               also sent as custom_form_override_reason, so the job's comment
               timeline still explains the completion most worth explaining. -->
          <TextArea
            v-model="reason"
            class="mt-4"
            :message="
              canOverrideCloseout
                ? 'Reason for overriding required forms'
                : 'Reason for status change'
            "
          />
        </div>
      </Modal>
    </div>
  </SafeTeleport>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { trackEvent } from "@/lib/pendo";
import { useAccessRole } from "@/lib/useAccessRole";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import Modal from "@/components/Modal.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Dropdown from "@/components/Seranova/Dropdown/Dropdown.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import CONSTANTS from "@/Constants";
import CustomFormCloseoutBlock from "@/CustomForms/CustomFormCloseoutBlock.vue";
import { AppointmentStatus, AppointmentV2 } from "@/typings/appointment";
import type { CustomFormCloseoutGap } from "@/typings/customForm";
import { GENERIC_ERROR_MESSAGE, reportApiFailure } from "@/vuex/interceptors";
const { APPOINTMENT_STATUS_ORDER, APPOINTMENT_STATUS_MAP } = CONSTANTS;
import { SafeTeleport } from "vue-safe-teleport";

import AppointmentTimeline from "./AppointmentTimeline.vue";
import { useAppointmentStatus } from "./useAppointmentStatus";

interface StatusChangeError {
  response?: {
    status?: number;
    data?: { message?: string; errors?: unknown };
  };
}

const emit = defineEmits(["refresh-appointment"]);
const props = defineProps<{ appointment: AppointmentV2 }>();
const { getRequiredFieldsForStatus, times, changeStatus, getTimesToRemove } =
  useAppointmentStatus(props.appointment);
const selectedStatus = ref<AppointmentStatus | null>(null);
const modalOpen = ref(false);
const reason = ref("");
const closeoutGaps = ref<CustomFormCloseoutGap[] | null>(null);
const overrideCloseout = ref(false);

const store = useStore();
const { canAccess } = useAccessRole();

// The gate refused this completion AND the admin ticked the waiver in the block.
// Both halves matter: without the tick, typing a note would waive the gate as a
// side effect, and the admin would never have made the decision explicitly.
//
// Deliberately NOT gated on an RBAC permission. canAccess() short-circuits to true
// whenever the rbac flag is off, so it could never actually gate anything — but
// where rbac IS on it checks for a permission row that exists only once
// Access::UpdatePermissionsJob has run, which hid this affordance from the
// dispatchers it was built for. Who may waive is enforced server-side, on every
// environment, regardless of rbac.
const canOverrideCloseout = computed(
  () => Boolean(closeoutGaps.value) && overrideCloseout.value
);

// featureEnabledForTenant rather than useFeatureFlag: the jbuilder omits a
// disabled flag's key entirely, and useFeatureFlag returns that raw lookup as
// undefined where this getter coerces to false.
const closeOutGateEnabled = computed(() =>
  store.getters.featureEnabledForTenant("custom_forms_close_out_gate")
);

// The V2 appointment payload nests the job; there is no flat job_id on it.
const jobId = computed(() => props.appointment.job?.id ?? null);

// Both conditions gate the Forms tab itself, so linking on either alone would
// point at a tab the admin cannot open.
const canOpenFormsTab = computed(
  () =>
    store.getters.featureEnabledForTenant("custom_forms_ff_enabled") &&
    canAccess("jobs")
);
const links = computed(() => props.appointment.links || {});
const statusOptions = computed(() => {
  const availableStatuses = [props.appointment.status].concat(
    Object.keys(links.value)
  );
  // Sort statuses by flow
  const sortedStatuses = APPOINTMENT_STATUS_ORDER.filter((status) =>
    availableStatuses.includes(status)
  );
  return sortedStatuses
    .filter(
      (status) =>
        status !== props.appointment.status &&
        APPOINTMENT_STATUS_MAP[status] &&
        links.value[status]
    )
    .map((status) => ({
      value: status,
      label: APPOINTMENT_STATUS_MAP[status]
    }));
});
const isDisabled = computed(() => statusOptions.value.length === 0);
const requiredFields = computed(() => {
  if (!selectedStatus.value) return [];
  return getRequiredFieldsForStatus(selectedStatus.value);
});
const lostFields = computed(() => {
  if (!selectedStatus.value) return [];
  return getTimesToRemove(props.appointment.status, selectedStatus.value);
});

const label = computed(() => {
  const statusKey = props.appointment.status;
  return APPOINTMENT_STATUS_MAP[statusKey] || statusKey;
});

const isMultiTechEnabled = useFeatureFlag("multiple_technicians_ff_enabled");

const TIME_FIELD_LABELS: Record<string, string> = {
  accepted_time: "Claimed",
  dispatch_time: "Travel Started",
  arrival_time: "Arrival",
  departure_time: "Departure"
};

const techImpact = computed(() => {
  if (!isMultiTechEnabled.value || !selectedStatus.value) return null;
  const ats = props.appointment.appointment_technicians || [];
  if (ats.length <= 1) return null;

  const techs = props.appointment.technicians || [];
  const techNameMap = new Map(techs.map((t) => [t.technician_id, t.full_name]));
  const hasCrewMembers = (props.appointment.crew_members || []).length > 0;
  const isDestructive = lostFields.value.length > 0;

  // Times being newly set (not already on the appointment)
  const fieldsBeingSet = requiredFields.value.filter(
    (f) => !props.appointment[f as keyof AppointmentV2]
  );

  const items = ats.map((at) => {
    const name =
      techNameMap.get(at.technician_profile_id) ||
      `Technician ${at.position + 1}`;
    const isPrimary = at.position === 0;

    if (isDestructive) {
      const clearedLabels = lostFields.value
        .map((f) => TIME_FIELD_LABELS[f])
        .join(", ");
      return {
        name,
        isPrimary,
        description: `${clearedLabels} will be cleared`
      };
    }

    if (isPrimary) {
      return { name, isPrimary, description: "times will be updated directly" };
    }

    const preserved = fieldsBeingSet.filter((f) => at[f as keyof typeof at]);
    const willReceive = fieldsBeingSet.filter((f) => !at[f as keyof typeof at]);

    if (preserved.length > 0 && willReceive.length > 0) {
      const preservedLabels = preserved
        .map((f) => TIME_FIELD_LABELS[f])
        .join(", ");
      const receiveLabels = willReceive
        .map((f) => TIME_FIELD_LABELS[f])
        .join(", ");
      return {
        name,
        isPrimary,
        description: `${receiveLabels} will be set; ${preservedLabels} already recorded individually`
      };
    }

    if (preserved.length > 0) {
      return {
        name,
        isPrimary,
        description: "all times already recorded individually — no changes"
      };
    }

    return { name, isPrimary, description: "times will be synced" };
  });

  return { items, isDestructive, hasCrewMembers };
});
const handleStatusSelect = (item: { value: string; label: string }) => {
  selectedStatus.value = item.value as AppointmentStatus;
  // The only path that opens the modal, so the only place stale gaps from a
  // previous block have to be dropped — along with a waiver ticked against them.
  closeoutGaps.value = null;
  overrideCloseout.value = false;
  modalOpen.value = true;
};
const hideModal = () => {
  modalOpen.value = false;
};

const hasFutureTime = (): boolean => {
  return requiredFields.value.some(
    (field) => times[field] && moment(times[field]).isAfter(moment())
  );
};

const handleSave = async () => {
  if (!selectedStatus.value) return;
  const statusLink = links.value[selectedStatus.value];
  if (!statusLink) {
    window.notify.error("Unable to edit the status of this appointment.");
    return;
  }
  if (hasFutureTime()) {
    window.notify.error("Cannot use future time.");
    return;
  }
  // Read once: handleSaveError repopulates closeoutGaps, so a read after the
  // await answers differently from one before it.
  const overriding = canOverrideCloseout.value;
  try {
    await changeStatus(
      selectedStatus.value,
      times,
      reason.value,
      statusLink,
      overriding ? reason.value : undefined
    );
  } catch (error) {
    handleSaveError(error);
    return;
  }
  // Tracked after the response, so this counts waivers that actually happened
  // rather than attempts the server refused.
  if (overriding) {
    trackEvent("custom_form_closeout_override", {
      appointment_id: props.appointment.id,
      unmet_form_count: closeoutGaps.value?.length ?? 0
    });
  }
  modalOpen.value = false;
  window.notify.success("Appointment status updated");
  emit("refresh-appointment");
};

// The request carries ignoreError, so the interceptor's flash is suppressed for
// every failure here, not just the gate's. Anything that is not a close-out block
// has to be surfaced from this side or it fails silently — including the RUM
// report, which ignoreError suppresses along with the flash.
const handleSaveError = (error: unknown) => {
  const gaps = closeoutGapsFrom(error);
  if (gaps) {
    closeoutGaps.value = gaps;
    return;
  }

  const message = asStatusChangeError(error).response?.data?.message;
  if (!message) reportApiFailure(error, "generic_error");
  window.notify.error(message || GENERIC_ERROR_MESSAGE);
};

// The gate is the only 409 on these endpoints answering with an `errors` array —
// every validation failure answers with `message` — so the shape discriminates it
// and the status code alone does not.
const closeoutGapsFrom = (error: unknown): CustomFormCloseoutGap[] | null => {
  if (!closeOutGateEnabled.value) return null;

  const response = asStatusChangeError(error).response;
  if (response?.status !== 409) return null;
  return Array.isArray(response.data?.errors)
    ? (response.data.errors as CustomFormCloseoutGap[])
    : null;
};

const asStatusChangeError = (error: unknown): StatusChangeError =>
  (error as StatusChangeError) ?? {};
const handleUpdateTime = (data: { [k: string]: string }) => {
  const fieldName = Object.keys(data)[0];
  times[fieldName] = data[fieldName];
};
</script>

<style scoped></style>
